import React, { createContext, useContext, useState, useEffect } from 'react'
import { User, AuthContextType } from '@/types'
import { api } from '@/lib/api'
import { useLeaderboardCache } from '@/hooks/useLeaderboardCache'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { preloadAllLeaderboards } = useLeaderboardCache()

  // 从本地缓存加载用户信息
  const loadCachedUser = () => {
    try {
      const cachedUser = localStorage.getItem('cached_user')
      if (cachedUser) {
        const userData = JSON.parse(cachedUser)
        // 检查缓存是否过期（24小时）
        const cacheTime = localStorage.getItem('cached_user_time')
        if (cacheTime && Date.now() - parseInt(cacheTime) < 24 * 60 * 60 * 1000) {
          setUser(userData)
          return true
        }
      }
    } catch (error) {
      console.warn('Failed to load cached user:', error)
    }
    return false
  }

  // 缓存用户信息
  const cacheUser = (userData: User) => {
    try {
      localStorage.setItem('cached_user', JSON.stringify(userData))
      localStorage.setItem('cached_user_time', Date.now().toString())
    } catch (error) {
      console.warn('Failed to cache user:', error)
    }
  }

  useEffect(() => {
    const token = localStorage.getItem('auth_token')

    if (token) {
      // 先尝试加载缓存的用户信息，提供即时体验
      const hasCachedUser = loadCachedUser()
      if (hasCachedUser) {
        setIsLoading(false) // 有缓存时立即停止加载状态
      }

      // 然后在后台验证最新的用户信息
      loadUser()
    } else {
      setIsLoading(false)
    }

    // 设置超时，防止无限加载（优化为10秒，平衡用户体验）
    const timeout = setTimeout(() => {
      console.warn('认证超时，停止加载')
      setIsLoading(false)
    }, 10000) // 10秒超时

    // 监听 localStorage 变化
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_token') {
        if (!e.newValue) {
          setUser(null)
          // 清除缓存
          localStorage.removeItem('cached_user')
          localStorage.removeItem('cached_user_time')
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      clearTimeout(timeout)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  const loadUser = async () => {
    try {
      const token = localStorage.getItem('auth_token')

      if (!token) {
        setIsLoading(false)
        return
      }

      const response = await api.getProfile()

      if (response.success && response.data) {
        setUser(response.data)
        cacheUser(response.data) // 缓存用户信息

        // 用户认证成功后，立即开始预加载排行榜数据
        setTimeout(() => {
          preloadAllLeaderboards()
        }, 100) // 延迟100ms，让页面先渲染
      } else {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('cached_user')
        localStorage.removeItem('cached_user_time')
      }
    } catch (error) {
      console.error('Failed to load user:', error)
      // 网络错误时不清除 token，保持缓存的用户信息
      if (error instanceof Error && error.name !== 'AbortError' && !(error instanceof TypeError)) {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('cached_user')
        localStorage.removeItem('cached_user_time')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (username: string, password: string) => {
    console.log('🔐 Attempting login for user:', username)
    const response = await api.login(username, password)
    console.log('🔐 Login response:', response)

    if (response.success && response.data) {
      console.log('✅ Login successful, storing token')
      console.log('🔑 Token length:', response.data.token.length)
      localStorage.setItem('auth_token', response.data.token)
      setUser(response.data.user)
      cacheUser(response.data.user) // 缓存用户信息
      console.log('👤 User set:', response.data.user.username)

      // 登录成功后立即预加载排行榜
      setTimeout(() => {
        preloadAllLeaderboards()
      }, 100)
    } else {
      console.log('❌ Login failed:', response.error)
      throw new Error(response.error || 'Login failed')
    }
  }

  const register = async (username: string, password: string, inviteCode: string) => {
    const response = await api.register(username, password, inviteCode)
    if (response.success && response.data) {
      localStorage.setItem('auth_token', response.data.token)
      setUser(response.data.user)
      cacheUser(response.data.user) // 缓存用户信息

      // 注册成功后立即预加载排行榜
      setTimeout(() => {
        preloadAllLeaderboards()
      }, 100)
    } else {
      throw new Error(response.error || 'Registration failed')
    }
  }

  const logout = async () => {
    try {
      console.log('🚪 Logging out user')
      await api.logout()
      console.log('✅ Server logout successful')
    } catch (error) {
      console.error('❌ Server logout failed:', error)
      // 即使服务器登出失败，也要清理本地状态
    } finally {
      console.log('🗑️ Clearing local auth state')
      localStorage.removeItem('auth_token')
      setUser(null)
    }
  }

  const updateProfile = async (data: Partial<User>) => {
    try {
      const response = await api.updateProfile(data)
      if (response.success) {
        setUser(response.data)
      }
    } catch (error) {
      console.error('Update profile error:', error)
      throw error
    }
  }

  const uploadAvatar = async (file: File): Promise<string> => {
    return await api.uploadAvatar(file)
  }

  const value: AuthContextType = {
    user,
    login,
    register,
    logout,
    updateProfile,
    uploadAvatar,
    isLoading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
